// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useCallback } from 'react';

import type { ProductId, ShopRole } from '@evolvemep/api-ts';
import { LoadingOverlay } from '@mantine/core';
import { Navigate } from 'react-router';

import type { WithRequiredNotNull } from 'helpers/helper-types';
import { isNil } from 'helpers/isNotNil';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { type MaybeUserContextType, useMaybeUser } from 'hooks/user/useMaybeUser';

type UserContextType = WithRequiredNotNull<MaybeUserContextType, 'user'> & {
  /** Returns true if the user has at least one of the required roles, or the `Company.Administrator` role */
  userHasRoles: (...roles: ShopRole[]) => boolean;
  /** Returns true if the user has at least one of the required licenses */
  userHasLicense: (...requiredLicenses: ProductId[]) => boolean;
};

const UserContext = React.createContext<UserContextType | undefined>(undefined);

type Props = {
  children: ReactNode;
};

export const UserProvider = ({ children }: Props) => {
  const { user, roles, licenses, ...context } = useMaybeUser();

  const userHasRoles = useCallback(
    (...checkForRoles: ShopRole[]) =>
      checkForRoles.some((r) => roles.includes(r)) || roles.includes('Company.Administrator'),
    [roles],
  );

  const userHasLicense = useCallback(
    (...requiredLicenses: ProductId[]) => requiredLicenses.some((license) => licenses.includes(license)),
    [licenses],
  );

  if (isNil(user)) {
    if (context.loading) {
      return <LoadingOverlay visible />;
    }
    const path = `${window.location.pathname}${window.location.search}`;
    const redirect = path !== '/' ? `?redirect=${encodeURIComponent(path)}` : '';
    return <Navigate to={`/auth/login${redirect}`} />;
  }

  return (
    <UserContext
      value={{
        ...context,
        userHasRoles,
        userHasLicense,
        user,
        roles,
        licenses,
      }}
    >
      {children}
    </UserContext>
  );
};

export const useUser = () => useGeneralContext(UserContext, 'User');
